import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    Card,
    CardContent,
    Grid,
    Rating,
    Avatar,
    IconButton,
    Menu,
    MenuItem,
} from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { Filter, ThumbsUp, ThumbsDown, Edit, MoreVertical, CheckCircle } from 'lucide-react';
import { ICompanyDetails, ICompanyReview } from '../../../redux/company/types';
import Pagination from '../../Common/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import {
    getCompanyDetails,
    likeCompanyReview,
    deleteCompanyReviewLike,
    editCompanyReview,
} from '../../../redux/company/actions';
import { useParams } from 'react-router-dom';
import { likingReviewSelector, userProfileSelector } from '../../../selectors/selectors';
import { ReviewFormModal } from '../ReviewFormModal';

interface IReviewsTab {
    companyDetails?: ICompanyDetails;
}

type SortOption = 'newest' | 'oldest' | 'highest' | 'lowest';

const ReviewsTab: React.FC<IReviewsTab> = ({ companyDetails }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const params = useParams();
    const { id } = params;
    const { company, reviews } = companyDetails || {};
    const [sortOption, setSortOption] = useState<SortOption>('newest');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedReview, setSelectedReview] = useState<ICompanyReview | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingReview, setEditingReview] = useState<ICompanyReview | null>(null);
    const likingReview = useSelector(likingReviewSelector);
    const currentUser = useSelector(userProfileSelector);

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const ratingData = [
        { label: 'Culture', value: (company?.avg_culture || 0) * 20, score: company?.avg_culture || 0 },
        {
            label: 'Professionalism',
            value: (company?.avg_professionalism || 0) * 20,
            score: company?.avg_professionalism || 0,
        },
        {
            label: 'Payment Experience',
            value: (company?.avg_payment_experience || 0) * 20,
            score: company?.avg_payment_experience || 0,
        },
        { label: 'Reliability', value: (company?.avg_reliability || 0) * 20, score: company?.avg_reliability || 0 },
        { label: 'Satisfaction', value: (company?.avg_satisfaction || 0) * 20, score: company?.avg_satisfaction || 0 },
        {
            label: 'Creative Quality',
            value: (company?.avg_creative_quality || 0) * 20,
            score: company?.avg_creative_quality || 0,
        },
    ];

    const sortedReviews = useMemo(() => {
        if (!reviews?.data) return [];
        const reviewsArray = [...reviews.data];

        switch (sortOption) {
            case 'newest':
                return reviewsArray.sort(
                    (a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime(),
                );
            case 'oldest':
                return reviewsArray.sort(
                    (a, b) => new Date(a.createdAt || '').getTime() - new Date(b.createdAt || '').getTime(),
                );
            case 'highest':
                return reviewsArray.sort((a, b) => b.rating - a.rating);
            case 'lowest':
                return reviewsArray.sort((a, b) => a.rating - b.rating);
            default:
                return reviewsArray;
        }
    }, [reviews?.data, sortOption]);

    const handleLike = (review: ICompanyReview) => {
        if (review.is_user_liked) {
            // Find the like object to delete
            const likeObject = review.user_like.find(like => like.user === currentUser._id);
            if (likeObject) {
                dispatch(
                    deleteCompanyReviewLike({
                        documentId: likeObject._id,
                        additionalPayload: {
                            reviewId: review._id,
                            like_type: 'like',
                        },
                    }),
                );
            }
        } else {
            dispatch(
                likeCompanyReview({
                    reviewId: review._id,
                    like_type: 'like',
                }),
            );
        }
    };

    const handleDislike = (review: ICompanyReview) => {
        if (review.is_user_disliked) {
            // Find the dislike object to delete
            const dislikeObject = review.user_dislike.find(dislike => dislike.user === currentUser._id);
            if (dislikeObject) {
                dispatch(
                    deleteCompanyReviewLike({
                        documentId: dislikeObject._id,
                        additionalPayload: {
                            reviewId: review._id,
                            like_type: 'dislike',
                        },
                    }),
                );
            }
        } else {
            dispatch(
                likeCompanyReview({
                    reviewId: review._id,
                    like_type: 'dislike',
                }),
            );
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, review: ICompanyReview) => {
        setAnchorEl(event.currentTarget);
        setSelectedReview(review);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedReview(null);
    };

    const handleEditReview = () => {
        if (selectedReview) {
            setEditingReview(selectedReview);
            setIsEditModalOpen(true);
        }
        handleMenuClose();
    };

    const handleEditSubmit = (formData: any) => {
        if (editingReview) {
            const payload = {
                ...formData,
                reviewId: editingReview._id,
                edited: true,
                edited_on: new Date().toISOString(),
            };
            dispatch(editCompanyReview(payload));
            setIsEditModalOpen(false);
            setEditingReview(null);
        }
    };

    const isCurrentUserReview = (review: ICompanyReview) => {
        return review.user._id === currentUser._id;
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <Box>
            {/* Overall Rating Section */}
            <Card
                sx={{
                    backgroundColor: theme[themeStyle].pure_black,
                    border: `1px solid ${theme[themeStyle].deep_grey_lighter}`,
                    borderRadius: 2,
                    mb: 3,
                }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                            mb: 3,
                        }}>
                        Overall Rating
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.BOLD_HEADER,
                                        fontSize: '2rem',
                                    }}>
                                    {company?.rating?.toFixed(1) || '0.0'}
                                </Typography>
                                <Rating
                                    value={company?.rating || 0}
                                    readOnly
                                    size="large"
                                    sx={{
                                        '& .MuiRating-iconFilled': {
                                            color: theme[themeStyle].highlight,
                                        },
                                        '& .MuiRating-iconHover': {
                                            color: theme[themeStyle].highlight,
                                        },
                                        '& .MuiRating-iconEmpty': {
                                            color: theme[themeStyle].highlight,
                                        },
                                    }}
                                />
                            </Box>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                    fontSize: '0.875rem',
                                }}>
                                Based on {company?.number_of_reviews || 0} reviews
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {ratingData.map((item, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.875rem',
                                            }}>
                                            {item.label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.875rem',
                                            }}>
                                            {item.score.toFixed(1)}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={item.value}
                                        sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            backgroundColor: theme[themeStyle].deep_grey_lighter,
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: theme[themeStyle].highlight,
                                                borderRadius: 4,
                                            },
                                        }}
                                    />
                                </Box>
                            ))}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Sort and Filter Section */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                }}>
                <Typography
                    sx={{
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.BOLD_HEADER,
                        fontSize: '1.25rem',
                    }}>
                    Reviews ({reviews?.pagination?.totalCount || 0})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Filter size={16} color={theme[themeStyle].tag_white} />
                    <select
                        value={sortOption}
                        onChange={e => setSortOption(e.target.value as SortOption)}
                        style={{
                            background: theme[themeStyle].pure_black,
                            color: theme[themeStyle].tag_white,
                            border: `1px solid ${theme[themeStyle].deep_grey_lighter}`,
                            borderRadius: '4px',
                            padding: '8px 12px',
                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                        }}>
                        <option value="newest">Newest</option>
                        <option value="oldest">Oldest</option>
                        <option value="highest">Highest Rated</option>
                        <option value="lowest">Lowest Rated</option>
                    </select>
                </Box>
            </Box>

            {/* Reviews List */}
            <Box sx={{ spaceY: 3 }}>
                {sortedReviews.length > 0 ? (
                    sortedReviews.map((review: ICompanyReview, index) => (
                        <Card
                            key={review._id}
                            sx={{
                                backgroundColor: theme[themeStyle].pure_black,
                                border: `1px solid ${theme[themeStyle].deep_grey_lighter}`,
                                borderRadius: 2,
                                mb: 3,
                            }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        mb: 2,
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                background: review.user.profile_picture
                                                    ? 'transparent'
                                                    : 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                                                fontSize: '0.875rem',
                                                fontWeight: 'bold',
                                            }}
                                            src={review.user.profile_picture}>
                                            {getInitials(`${review.user.firstName} ${review.user.lastName}`)}
                                        </Avatar>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                        fontWeight: 'medium',
                                                        fontSize: '0.875rem',
                                                    }}>
                                                    {`${review.user.firstName} ${review.user.lastName}`}
                                                </Typography>
                                                {review.verified && (
                                                    <CheckCircle
                                                        size={16}
                                                        color={theme[themeStyle].highlight}
                                                        style={{ marginLeft: '4px' }}
                                                    />
                                                )}
                                            </Box>
                                            <Rating
                                                value={review.rating}
                                                readOnly
                                                size="small"
                                                sx={{
                                                    '& .MuiRating-iconFilled': {
                                                        color: theme[themeStyle].highlight,
                                                    },
                                                    '& .MuiRating-iconHover': {
                                                        color: theme[themeStyle].highlight,
                                                    },
                                                    '& .MuiRating-iconEmpty': {
                                                        color: theme[themeStyle].highlight,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {isCurrentUserReview(review) && (
                                            <IconButton
                                                onClick={e => handleMenuOpen(e, review)}
                                                sx={{ color: theme[themeStyle].tag_white }}>
                                                <MoreVertical size={16} />
                                            </IconButton>
                                        )}
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.75rem',
                                            }}>
                                            {formatDate(review.createdAt)}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.BOLD_HEADER,
                                        fontSize: '1rem',
                                        mb: 1,
                                    }}>
                                    {review.review_subject}
                                </Typography>

                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        sx={{
                                            color: '#10B981',
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            fontWeight: 'medium',
                                            mb: 1,
                                            fontSize: '0.875rem',
                                        }}>
                                        Pros
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            fontSize: '0.875rem',
                                        }}>
                                        {review.pros || '-'}
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        sx={{
                                            color: '#EF4444',
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            fontWeight: 'medium',
                                            mb: 1,
                                            fontSize: '0.875rem',
                                        }}>
                                        Cons
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                            fontSize: '0.875rem',
                                        }}>
                                        {review.cons || '-'}
                                    </Typography>
                                </Box>

                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        fontSize: '0.75rem',
                                        mt: 2,
                                        mb: 2,
                                    }}>
                                    {review.role || 'Employee'} • {review.time || 'Current Employee'} •{' '}
                                    {review.location?.address || 'Unknown Location'} • {formatDate(review.createdAt)}
                                    {review.edited && (
                                        <span style={{ color: theme[themeStyle].lightWhite, opacity: 0.7 }}>
                                            {' '}
                                            • Edited {review.edited_on ? formatDate(review.edited_on) : ''}
                                        </span>
                                    )}
                                </Typography>

                                {/* Like/Dislike Section */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mt: 2,
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconButton
                                                onClick={() => handleLike(review)}
                                                disabled={likingReview}
                                                sx={{
                                                    color: review.is_user_liked
                                                        ? theme[themeStyle].highlight
                                                        : theme[themeStyle].tag_white,
                                                    '&:hover': {
                                                        color: theme[themeStyle].highlight,
                                                    },
                                                }}>
                                                <ThumbsUp size={16} />
                                            </IconButton>
                                            {review.total_likes && review.total_likes > 0 && (
                                                <Typography
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                        fontSize: '0.75rem',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                    }}>
                                                    {review.total_likes}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconButton
                                                onClick={() => handleDislike(review)}
                                                disabled={likingReview}
                                                sx={{
                                                    color: review.is_user_disliked
                                                        ? theme[themeStyle].primary_red
                                                        : theme[themeStyle].tag_white,
                                                    '&:hover': {
                                                        color: theme[themeStyle].primary_red,
                                                    },
                                                }}>
                                                <ThumbsDown size={16} />
                                            </IconButton>
                                            {review.total_dislikes && review.total_dislikes > 0 && (
                                                <Typography
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                        fontSize: '0.75rem',
                                                        minWidth: '20px',
                                                        textAlign: 'center',
                                                    }}>
                                                    {review.total_dislikes}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))
                ) : (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 4,
                            color: theme[themeStyle].tag_white,
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                fontSize: '1rem',
                                color: theme[themeStyle].tag_white,
                            }}>
                            No reviews yet. Be the first to review this company!
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Pagination */}
            {reviews?.pagination && reviews.pagination.totalPages > 1 && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        currentPage={reviews.pagination.currentPage}
                        totalPages={reviews.pagination.totalPages}
                        onPageChange={page => {
                            if (id) {
                                dispatch(
                                    getCompanyDetails({
                                        slug: id,
                                        withReviews: true,
                                        reviewsPage: page,
                                        reviewsLimit: 3,
                                        singleFetch: true,
                                    }),
                                );
                            }
                        }}
                    />
                </Box>
            )}

            {/* Edit Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        backgroundColor: theme[themeStyle].dropdown_black,
                        border: `1px solid ${theme[themeStyle].form_divider}`,
                        borderRadius: '8px',
                    },
                }}>
                <MenuItem
                    onClick={handleEditReview}
                    sx={{
                        color: theme[themeStyle].primary_white,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].form_divider,
                        },
                    }}>
                    <Edit size={16} style={{ marginRight: '8px' }} />
                    Edit Review
                </MenuItem>
            </Menu>

            {/* Edit Review Modal */}
            {editingReview && (
                <ReviewFormModal
                    open={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setEditingReview(null);
                    }}
                    companyId={company?._id || ''}
                    companyName={company?.company_name || ''}
                    editingReview={editingReview}
                    onSubmit={handleEditSubmit}
                />
            )}
        </Box>
    );
};

export { ReviewsTab };
